import React from "react"
import PropTypes from "prop-types"
import L10n from "lib/carrot-l10n"
import WizardWrapper from "components/views/atoms/forms/WizardWrapper"
import AttachmentStates from "utils/AttachmentStates"
import { HR, Spacer } from "components/views/atoms/Atoms"
import { PageHeader } from "components/views/molecules/Molecules"
import { MasterLayout } from "components/views/atoms/layouts/MasterLayout"
import { BodyWithRightSidebarLayout } from "components/views/atoms/layouts/BodyWithRightSidebarLayout"
import { Redirect } from "react-router-dom"
import { useSelector } from "react-redux"
import { UploadSidebar } from "components/views/reimbursements/upload/UploadSidebar"
import { UploadCardExpenseDetails } from "components/views/reimbursements/upload/UploadCardExpenseDetails"
import { UploadFile } from "components/views/reimbursements/upload/UploadFile"
import { UploadDropzoneWithIllustration } from "components/views/reimbursements/upload/UploadDropzoneWithIllustration"
import { StringParam, useQueryParam } from "use-query-params"
import { CarrotQueryParam } from "utils/CarrotUrls"
import { ReimbursementsModalContainer } from "../../../containers/reimbursements/ReimbursementsModalContainer"
import { getShouldFillOutBankInfo } from "derivedSelectors"
import { useBenefitEnrollment } from "../../../context/enrollment/BenefitEnrollmentContext"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()
const { COMPLETED } = AttachmentStates

const UploadMainContent = ({
  expenseId,
  reimbursements,
  draggingFile,
  onDone,
  onDelete,
  onRetry,
  onDropAccepted,
  onDropRejected,
  attachments,
  onClose
}) => {
  const [reimbursementType] = useQueryParam(CarrotQueryParam.ReimbursementType, StringParam)

  const uploadsComplete = () => attachments.length > 0 && attachments.every((x) => x.state === COMPLETED)

  const handleSubmit = (reimbursementQueryString) => {
    onDone(expenseId, reimbursementQueryString)
  }

  const { needsBenefitEnrollment } = useBenefitEnrollment()
  const currentReimbursement = reimbursements.find((reimbursement) => reimbursement.expenseId === parseInt(expenseId))
  const shouldShowBankInfoModal = useSelector(getShouldFillOutBankInfo)

  if (needsBenefitEnrollment) {
    return <Redirect to={"/#needs-benefit-enrollment"} />
  }

  if ((!currentReimbursement || !currentReimbursement.isCardExpense) && shouldShowBankInfoModal) {
    return <Redirect to={"/#no-bank-info"} />
  }

  if (reimbursementType == null) {
    return <ReimbursementsModalContainer onClose={onClose} expenseId={expenseId} />
  }

  return (
    <>
      {expenseId && (
        <>
          <Spacer height={0.3} />
          <UploadCardExpenseDetails
            submittedDatetime={currentReimbursement?.submittedDatetime}
            amount={currentReimbursement?.amount}
          />
        </>
      )}
      <WizardWrapper
        done
        sending={!uploadsComplete()}
        onDone={() => handleSubmit(reimbursementType)}
        doneText={l`next`}
        sendingText={l`next`}
        nextId="upload-next-button"
      >
        <div>
          <UploadDropzoneWithIllustration
            draggingFile={draggingFile}
            onDropAccepted={onDropAccepted}
            onDropRejected={onDropRejected}
            small={attachments.length > 0}
          />
          <Spacer height={1.5} />

          {attachments.map((attachment) => (
            <UploadFile key={attachment.guid} attachment={attachment} onDelete={onDelete} onRetry={onRetry} />
          ))}
          {attachments && attachments.length > 0 && (
            <>
              <HR color="black-10" />
              <Spacer height={1.5} />
            </>
          )}
        </div>
      </WizardWrapper>
      <Spacer height={2} />
      <HR color="black-10" />
      <Spacer height={2} />
    </>
  )
}

const Upload = ({
  attachments,
  expenseId,
  onDone,
  onDelete,
  onRetry,
  draggingFile,
  onDropAccepted,
  onDropRejected,
  reimbursements,
  onClose
}) => {
  const header = (
    <PageHeader
      title={expenseId ? l`client.reimbursements.uploadStatement` : l`Get reimbursed`}
      icon="icn-get-reimbursed"
    />
  )
  return (
    <MasterLayout header={header}>
      <BodyWithRightSidebarLayout sidebar={<UploadSidebar expenseId={expenseId} />}>
        <UploadMainContent
          expenseId={expenseId}
          reimbursements={reimbursements}
          draggingFile={draggingFile}
          onDone={onDone}
          onDelete={onDelete}
          onRetry={onRetry}
          onDropAccepted={onDropAccepted}
          onDropRejected={onDropRejected}
          attachments={attachments}
          onClose={onClose}
        />
      </BodyWithRightSidebarLayout>
    </MasterLayout>
  )
}

Upload.propTypes = {
  attachments: PropTypes.array.isRequired,
  onDropAccepted: PropTypes.func.isRequired,
  draggingFile: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  expenseId: PropTypes.string,
  reimbursements: PropTypes.array.isRequired,
  onClose: PropTypes.func
}

export default Upload
